/**
 * Template Manager Component
 * 
 * Comprehensive template management interface with CRUD operations,
 * preview capabilities, and template organization features
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
    Container, 
    Row, 
    Col, 
    Card, 
    Button, 
    Form, 
    Modal, 
    Dropdown,
    Badge,
    Alert,
    Spinner,
    Accordion,
    ListGroup,
    Tab,
    Tabs,
    InputGroup,
    Table,
    OverlayTrigger,
    Tooltip
} from 'react-bootstrap';
import { 
    BsBarChart, 
    BsGear, 
    BsPlus,
    BsSearch,
    BsFilter,
    BsGrid,
    BsList,
    BsEye,
    BsPencil,
    BsTrash,
    BsCopy,
    BsDownload,
    BsUpload,
    BsFileText,
    BsCheck,
    BsX,
    BsExclamationTriangle,
    BsInfoCircle,
    BsCheckCircle,
    BsXCircle,
    BsThreeDotsVertical,
    BsSave,
    BsArrowUp,
    BsArrowDown
} from 'react-icons/bs';
import { toast } from 'react-hot-toast';
import { ErrorBoundary, ServiceStatus, ReportingFallback, TemplateCard } from './shared';
import {
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    cloneTemplate,
    generateReportFromTemplate,
    selectTemplates,
    selectTemplatesLoading,
    setViewMode,
    setSearchQuery,
    selectViewMode
} from '../../redux/slices/reportingSlice';
import reportingService from '../../services/reportingService';

const TemplateManager = () => {
    const dispatch = useDispatch();
    
    // Redux state
    const templates = useSelector(selectTemplates);
    const loading = useSelector(selectTemplatesLoading);
    const viewMode = useSelector(selectViewMode);
    
    // Local state
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const [selectedTemplates, setSelectedTemplates] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [currentTemplate, setCurrentTemplate] = useState(null);
    const [templateForm, setTemplateForm] = useState({
        name: '',
        description: '',
        category: 'patient',
        subcategory: '',
        is_active: true,
        available_filters: [],
        required_permissions: [],
        query_template: '',
        output_template: ''
    });
    const [previewData, setPreviewData] = useState(null);
    const [statistics, setStatistics] = useState(null);
    
    // Categories for filtering and creation
    const categories = [
        { value: 'patient', label: 'Patient Reports' },
        { value: 'clinical', label: 'Clinical Reports' },
        { value: 'financial', label: 'Financial Reports' },
        { value: 'operational', label: 'Operational Reports' },
        { value: 'administrative', label: 'Administrative Reports' },
        { value: 'medpass', label: 'Medication Reports' },
        { value: 'hemodialysis', label: 'Hemodialysis Reports' },
        { value: 'facility', label: 'Facility Reports' },
        { value: 'graph', label: 'Analytics Reports' }
    ];
    
    // Load initial data
    useEffect(() => {
        loadTemplates();
        loadStatistics();
    }, []);
    
    // Load templates with current filters
    const loadTemplates = async () => {
        const params = {
            search: searchTerm || undefined,
            category: filterCategory !== 'all' ? filterCategory : undefined,
            is_active: filterStatus !== 'all' ? (filterStatus === 'active') : undefined,
            sort_by: sortBy,
            sort_order: sortOrder,
            limit: 50
        };
        
        await dispatch(fetchTemplates(params));
    };
    
    // Load template statistics
    const loadStatistics = async () => {
        try {
            const response = await reportingService.getTemplateStatistics();
            if (response.success) {
                setStatistics(response.data);
            }
        } catch (error) {
        }
    };
    
    // Reload templates when filters change
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            loadTemplates();
        }, 300);
        
        return () => clearTimeout(debounceTimer);
    }, [searchTerm, filterCategory, filterStatus, sortBy, sortOrder]);
    
    // Handle search
    const handleSearch = (term) => {
        setSearchTerm(term);
        dispatch(setSearchQuery(term));
    };
    
    // Handle view mode change
    const handleViewModeChange = (mode) => {
        dispatch(setViewMode(mode));
    };
    
    // Handle template selection
    const handleTemplateSelect = (templateId, isSelected) => {
        if (isSelected) {
            setSelectedTemplates(prev => [...prev, templateId]);
        } else {
            setSelectedTemplates(prev => prev.filter(id => id !== templateId));
        }
    };
    
    // Handle select all
    const handleSelectAll = (isSelected) => {
        if (isSelected) {
            setSelectedTemplates(filteredTemplates.map(t => t.id));
        } else {
            setSelectedTemplates([]);
        }
    };
    
    // Create new template
    const handleCreateTemplate = () => {
        setTemplateForm({
            name: '',
            description: '',
            category: 'patient',
            subcategory: '',
            is_active: true,
            available_filters: [],
            required_permissions: [],
            query_template: '',
            output_template: ''
        });
        setCurrentTemplate(null);
        setShowCreateModal(true);
    };
    
    // Edit template
    const handleEditTemplate = (template) => {
        setTemplateForm({
            name: template.name,
            description: template.description || '',
            category: template.category,
            subcategory: template.subcategory || '',
            is_active: template.is_active,
            available_filters: template.available_filters || [],
            required_permissions: template.required_permissions || [],
            query_template: template.query_template || '',
            output_template: template.output_template || ''
        });
        setCurrentTemplate(template);
        setShowEditModal(true);
    };
    
    // Preview template
    const handlePreviewTemplate = async (template) => {
        try {
            setCurrentTemplate(template);
            const response = await reportingService.previewTemplate(template.id);
            if (response.success) {
                setPreviewData(response.data);
                setShowPreviewModal(true);
            } else {
                toast.error('Failed to generate template preview');
            }
        } catch (error) {
            toast.error('Failed to generate template preview');
        }
    };
    
    // Clone template
    const handleCloneTemplate = async (template) => {
        try {
            const response = await dispatch(cloneTemplate({
                templateId: template.id,
                cloneData: {
                    name: `${template.name} (Copy)`,
                    description: `Copy of ${template.name}`
                }
            }));
            
            if (response.meta.requestStatus === 'fulfilled') {
                toast.success('Template cloned successfully');
                loadTemplates();
            }
        } catch (error) {
            toast.error('Failed to clone template');
        }
    };
    
    // Delete template
    const handleDeleteTemplate = (template) => {
        setCurrentTemplate(template);
        setShowDeleteModal(true);
    };
    
    // Confirm delete
    const confirmDelete = async () => {
        try {
            await dispatch(deleteTemplate(currentTemplate.id));
            toast.success('Template deleted successfully');
            setShowDeleteModal(false);
            setCurrentTemplate(null);
            loadTemplates();
        } catch (error) {
            toast.error('Failed to delete template');
        }
    };
    
    // Bulk delete
    const handleBulkDelete = async () => {
        if (selectedTemplates.length === 0) {
            toast.error('Please select templates to delete');
            return;
        }
        
        if (window.confirm(`Are you sure you want to delete ${selectedTemplates.length} templates?`)) {
            try {
                await Promise.all(selectedTemplates.map(id => dispatch(deleteTemplate(id))));
                toast.success(`${selectedTemplates.length} templates deleted successfully`);
                setSelectedTemplates([]);
                loadTemplates();
            } catch (error) {
                toast.error('Failed to delete some templates');
            }
        }
    };
    
    // Save template (create or update)
    const handleSaveTemplate = async () => {
        try {
            if (!templateForm.name.trim()) {
                toast.error('Please enter a template name');
                return;
            }
            
            const templateData = {
                ...templateForm,
                available_filters: templateForm.available_filters.filter(f => f.trim()),
                required_permissions: templateForm.required_permissions.filter(p => p.trim())
            };
            
            if (currentTemplate) {
                // Update existing template
                const response = await dispatch(updateTemplate({
                    templateId: currentTemplate.id,
                    templateData
                }));
                
                if (response.meta.requestStatus === 'fulfilled') {
                    toast.success('Template updated successfully');
                    setShowEditModal(false);
                }
            } else {
                // Create new template
                const response = await dispatch(createTemplate(templateData));
                
                if (response.meta.requestStatus === 'fulfilled') {
                    toast.success('Template created successfully');
                    setShowCreateModal(false);
                }
            }
            
            loadTemplates();
            setCurrentTemplate(null);
        } catch (error) {
            toast.error(`Failed to ${currentTemplate ? 'update' : 'create'} template`);
        }
    };
    
    // Generate report from template
    const handleGenerateReport = async (template) => {
        try {
            const reportRequest = {
                template_id: template.id,
                filters: {
                    conditions: [],
                    logic_operator: 'and',
                    date_range: {
                        start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                        end_date: new Date().toISOString().split('T')[0]
                    }
                },
                export_format: 'json',
                parameters: {
                    name: `${template.name} - Generated Report`,
                    description: `Report generated from ${template.name} template`
                }
            };
            
            const response = await dispatch(generateReportFromTemplate(reportRequest));
            if (response.meta.requestStatus === 'fulfilled') {
                toast.success('Report generation started successfully');
            }
        } catch (error) {
            toast.error('Failed to generate report');
        }
    };
    
    // Update form field
    const updateFormField = (field, value) => {
        setTemplateForm(prev => ({
            ...prev,
            [field]: value
        }));
    };
    
    // Add filter to template
    const addFilter = (filter) => {
        if (filter.trim() && !templateForm.available_filters.includes(filter.trim())) {
            setTemplateForm(prev => ({
                ...prev,
                available_filters: [...prev.available_filters, filter.trim()]
            }));
        }
    };
    
    // Remove filter from template
    const removeFilter = (index) => {
        setTemplateForm(prev => ({
            ...prev,
            available_filters: prev.available_filters.filter((_, i) => i !== index)
        }));
    };
    
    // Add permission to template
    const addPermission = (permission) => {
        if (permission.trim() && !templateForm.required_permissions.includes(permission.trim())) {
            setTemplateForm(prev => ({
                ...prev,
                required_permissions: [...prev.required_permissions, permission.trim()]
            }));
        }
    };
    
    // Remove permission from template
    const removePermission = (index) => {
        setTemplateForm(prev => ({
            ...prev,
            required_permissions: prev.required_permissions.filter((_, i) => i !== index)
        }));
    };
    
    // Filter templates based on current filters
    const filteredTemplates = templates?.filter(template => {
        const matchesSearch = !searchTerm || 
            template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (template.description && template.description.toLowerCase().includes(searchTerm.toLowerCase()));
        
        const matchesCategory = filterCategory === 'all' || template.category === filterCategory;
        const matchesStatus = filterStatus === 'all' || 
            (filterStatus === 'active' && template.is_active) ||
            (filterStatus === 'inactive' && !template.is_active);
        
        return matchesSearch && matchesCategory && matchesStatus;
    }) || [];
    
    return (
        <ErrorBoundary fallbackMessage="Unable to load the template manager. Please refresh the page or contact support.">
            <ServiceStatus 
                fallbackComponent={<ReportingFallback />}
                showStatus={true}
                autoRetry={false}
            >
                <Container fluid className="p-4">
                    {/* Header */}
                    <Row className="align-items-center mb-4">
                        <Col lg={8}>
                            <div>
                                <h2 className="text-primary fw-bold mb-1">Template Manager</h2>
                                <p className="text-muted mb-0">
                                    Create, edit, and manage report templates for your organization
                                </p>
                            </div>
                        </Col>
                        <Col lg={4} className="text-end">
                            <div className="d-flex gap-2">
                                <Button 
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={handleBulkDelete}
                                    disabled={selectedTemplates.length === 0}
                                >
                                    <BsTrash className="me-1" />
                                    Delete ({selectedTemplates.length})
                                </Button>
                                <Button 
                                    variant="primary"
                                    size="sm"
                                    onClick={handleCreateTemplate}
                                >
                                    <BsPlus className="me-1" />
                                    New Template
                                </Button>
                            </div>
                        </Col>
                    </Row>
                    
                    {/* Statistics Cards */}
                    {statistics && (
                        <Row className="mb-4">
                            <Col lg={3} md={6} className="mb-3">
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Body className="text-center">
                                        <div className="d-flex align-items-center justify-content-center mb-2">
                                            <BsBarChart className="h3 text-primary me-2" />
                                            <span className="h4 mb-0">{statistics.total_templates || 0}</span>
                                        </div>
                                        <small className="text-muted">Total Templates</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={3} md={6} className="mb-3">
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Body className="text-center">
                                        <div className="d-flex align-items-center justify-content-center mb-2">
                                            <BsCheckCircle className="h3 text-success me-2" />
                                            <span className="h4 mb-0">{statistics.active_templates || 0}</span>
                                        </div>
                                        <small className="text-muted">Active Templates</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={3} md={6} className="mb-3">
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Body className="text-center">
                                        <div className="d-flex align-items-center justify-content-center mb-2">
                                            <BsFileText className="h3 text-info me-2" />
                                            <span className="h4 mb-0">{statistics.reports_generated || 0}</span>
                                        </div>
                                        <small className="text-muted">Reports Generated</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={3} md={6} className="mb-3">
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Body className="text-center">
                                        <div className="d-flex align-items-center justify-content-center mb-2">
                                            <BsGear className="h3 text-warning me-2" />
                                            <span className="h4 mb-0">{categories.length}</span>
                                        </div>
                                        <small className="text-muted">Categories</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}
                    
                    {/* Controls */}
                    <Row className="mb-4">
                        <Col>
                            <Card className="border-0 shadow-sm">
                                <Card.Body className="py-3">
                                    <Row className="align-items-center">
                                        <Col lg={4}>
                                            <InputGroup>
                                                <InputGroup.Text>
                                                    <BsSearch />
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Search templates..."
                                                    value={searchTerm}
                                                    onChange={(e) => handleSearch(e.target.value)}
                                                />
                                            </InputGroup>
                                        </Col>
                                        <Col lg={2}>
                                            <Form.Select
                                                value={filterCategory}
                                                onChange={(e) => setFilterCategory(e.target.value)}
                                            >
                                                <option value="all">All Categories</option>
                                                {categories.map(cat => (
                                                    <option key={cat.value} value={cat.value}>
                                                        {cat.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Col>
                                        <Col lg={2}>
                                            <Form.Select
                                                value={filterStatus}
                                                onChange={(e) => setFilterStatus(e.target.value)}
                                            >
                                                <option value="all">All Status</option>
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                            </Form.Select>
                                        </Col>
                                        <Col lg={2}>
                                            <Dropdown>
                                                <Dropdown.Toggle variant="outline-secondary" size="sm">
                                                    <BsFilter className="me-1" />
                                                    Sort by {sortBy === 'created_at' ? 'Date' : 'Name'}
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <Dropdown.Item onClick={() => { setSortBy('created_at'); setSortOrder('desc'); }}>
                                                        <BsArrowDown className="me-1" />Newest First
                                                    </Dropdown.Item>
                                                    <Dropdown.Item onClick={() => { setSortBy('created_at'); setSortOrder('asc'); }}>
                                                        <BsArrowUp className="me-1" />Oldest First
                                                    </Dropdown.Item>
                                                    <Dropdown.Item onClick={() => { setSortBy('name'); setSortOrder('asc'); }}>
                                                        <BsArrowUp className="me-1" />Name A-Z
                                                    </Dropdown.Item>
                                                    <Dropdown.Item onClick={() => { setSortBy('name'); setSortOrder('desc'); }}>
                                                        <BsArrowDown className="me-1" />Name Z-A
                                                    </Dropdown.Item>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </Col>
                                        <Col lg={2} className="text-end">
                                            <div className="btn-group" role="group">
                                                <Button
                                                    variant={viewMode === 'grid' ? 'primary' : 'outline-secondary'}
                                                    size="sm"
                                                    onClick={() => handleViewModeChange('grid')}
                                                >
                                                    <BsGrid />
                                                </Button>
                                                <Button
                                                    variant={viewMode === 'list' ? 'primary' : 'outline-secondary'}
                                                    size="sm"
                                                    onClick={() => handleViewModeChange('list')}
                                                >
                                                    <BsList />
                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    
                    {/* Templates Grid/List */}
                    <Row>
                        <Col>
                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" className="text-primary" />
                                    <p className="mt-2 text-muted">Loading templates...</p>
                                </div>
                            ) : filteredTemplates.length > 0 ? (
                                <div>
                                    {/* Select All Checkbox */}
                                    <div className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            label={`Select All (${filteredTemplates.length} templates)`}
                                            checked={selectedTemplates.length === filteredTemplates.length && filteredTemplates.length > 0}
                                            onChange={(e) => handleSelectAll(e.target.checked)}
                                        />
                                    </div>
                                    
                                    {/* Templates Display */}
                                    <div className={`templates-container ${viewMode === 'list' ? 'list-view' : 'grid-view'}`}>
                                        <Row className={viewMode === 'grid' ? 'g-3' : 'g-2'}>
                                            {filteredTemplates.map(template => (
                                                <Col 
                                                    key={template.id} 
                                                    lg={viewMode === 'grid' ? 4 : 12} 
                                                    md={viewMode === 'grid' ? 6 : 12}
                                                >
                                                    <TemplateCard
                                                        template={template}
                                                        viewMode={viewMode}
                                                        onPreview={handlePreviewTemplate}
                                                        onUse={handleGenerateReport}
                                                        onEdit={handleEditTemplate}
                                                        onClone={handleCloneTemplate}
                                                        onDelete={handleDeleteTemplate}
                                                        isSelected={selectedTemplates.includes(template.id)}
                                                        onSelect={handleTemplateSelect}
                                                    />
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-5">
                                    <BsBarChart className="display-4 text-muted mb-3" />
                                    <h5 className="text-muted">No Templates Found</h5>
                                    <p className="text-muted mb-4">
                                        {searchTerm || filterCategory !== 'all' || filterStatus !== 'all'
                                            ? 'No templates match your current filters. Try adjusting your search criteria.'
                                            : 'You haven\'t created any templates yet. Create your first template to get started.'
                                        }
                                    </p>
                                    <Button variant="primary" onClick={handleCreateTemplate}>
                                        <BsPlus className="me-1" />
                                        Create Your First Template
                                    </Button>
                                </div>
                            )}
                        </Col>
                    </Row>
                </Container>
                
                {/* Create/Edit Template Modal */}
                <Modal show={showCreateModal || showEditModal} onHide={() => { setShowCreateModal(false); setShowEditModal(false); }} size="lg" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {currentTemplate ? 'Edit Template' : 'Create New Template'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Template Name *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter template name"
                                            value={templateForm.name}
                                            onChange={(e) => updateFormField('name', e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Category *</Form.Label>
                                        <Form.Select
                                            value={templateForm.category}
                                            onChange={(e) => updateFormField('category', e.target.value)}
                                        >
                                            {categories.map(cat => (
                                                <option key={cat.value} value={cat.value}>
                                                    {cat.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Subcategory</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter subcategory (optional)"
                                            value={templateForm.subcategory}
                                            onChange={(e) => updateFormField('subcategory', e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Status</Form.Label>
                                        <div className="pt-2">
                                            <Form.Check
                                                type="switch"
                                                label={templateForm.is_active ? 'Active' : 'Inactive'}
                                                checked={templateForm.is_active}
                                                onChange={(e) => updateFormField('is_active', e.target.checked)}
                                            />
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            <Form.Group className="mb-3">
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Enter template description"
                                    value={templateForm.description}
                                    onChange={(e) => updateFormField('description', e.target.value)}
                                />
                            </Form.Group>
                            
                            <Tabs defaultActiveKey="filters" className="mb-3">
                                <Tab eventKey="filters" title="Available Filters">
                                    <div className="mt-3">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Add Filter Field</Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter filter field name"
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            addFilter(e.target.value);
                                                            e.target.value = '';
                                                        }
                                                    }}
                                                />
                                                <Button
                                                    variant="outline-primary"
                                                    onClick={(e) => {
                                                        const input = e.target.closest('.input-group').querySelector('input');
                                                        addFilter(input.value);
                                                        input.value = '';
                                                    }}
                                                >
                                                    <BsPlus />
                                                </Button>
                                            </InputGroup>
                                        </Form.Group>
                                        
                                        {templateForm.available_filters.length > 0 && (
                                            <div>
                                                <h6>Current Filters:</h6>
                                                <div className="d-flex flex-wrap gap-2">
                                                    {templateForm.available_filters.map((filter, index) => (
                                                        <Badge key={index} bg="primary" className="d-flex align-items-center">
                                                            {filter}
                                                            <Button
                                                                variant="link"
                                                                size="sm"
                                                                className="text-white p-0 ms-1"
                                                                onClick={() => removeFilter(index)}
                                                            >
                                                                <BsX />
                                                            </Button>
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Tab>
                                
                                <Tab eventKey="permissions" title="Required Permissions">
                                    <div className="mt-3">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Add Permission</Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter required permission"
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            addPermission(e.target.value);
                                                            e.target.value = '';
                                                        }
                                                    }}
                                                />
                                                <Button
                                                    variant="outline-primary"
                                                    onClick={(e) => {
                                                        const input = e.target.closest('.input-group').querySelector('input');
                                                        addPermission(input.value);
                                                        input.value = '';
                                                    }}
                                                >
                                                    <BsPlus />
                                                </Button>
                                            </InputGroup>
                                        </Form.Group>
                                        
                                        {templateForm.required_permissions.length > 0 && (
                                            <div>
                                                <h6>Current Permissions:</h6>
                                                <div className="d-flex flex-wrap gap-2">
                                                    {templateForm.required_permissions.map((permission, index) => (
                                                        <Badge key={index} bg="warning" className="d-flex align-items-center">
                                                            {permission}
                                                            <Button
                                                                variant="link"
                                                                size="sm"
                                                                className="text-dark p-0 ms-1"
                                                                onClick={() => removePermission(index)}
                                                            >
                                                                <BsX />
                                                            </Button>
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Tab>
                            </Tabs>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => { setShowCreateModal(false); setShowEditModal(false); }}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleSaveTemplate}>
                            <BsSave className="me-1" />
                            {currentTemplate ? 'Update' : 'Create'} Template
                        </Button>
                    </Modal.Footer>
                </Modal>
                
                {/* Preview Modal */}
                <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="xl" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Template Preview: {currentTemplate?.name}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {previewData ? (
                            <div>
                                <Tabs defaultActiveKey="overview">
                                    <Tab eventKey="overview" title="Overview">
                                        <div className="mt-3">
                                            <Row>
                                                <Col md={6}>
                                                    <Card className="h-100">
                                                        <Card.Header>
                                                            <h6 className="mb-0">Template Details</h6>
                                                        </Card.Header>
                                                        <Card.Body>
                                                            <div className="mb-2">
                                                                <strong>Name:</strong> {currentTemplate?.name}
                                                            </div>
                                                            <div className="mb-2">
                                                                <strong>Category:</strong> 
                                                                <Badge bg="primary" className="ms-1">
                                                                    {currentTemplate?.category}
                                                                </Badge>
                                                            </div>
                                                            <div className="mb-2">
                                                                <strong>Status:</strong> 
                                                                <Badge bg={currentTemplate?.is_active ? 'success' : 'secondary'} className="ms-1">
                                                                    {currentTemplate?.is_active ? 'Active' : 'Inactive'}
                                                                </Badge>
                                                            </div>
                                                            <div className="mb-2">
                                                                <strong>Description:</strong>
                                                                <p className="text-muted small mt-1">
                                                                    {currentTemplate?.description || 'No description available'}
                                                                </p>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                                <Col md={6}>
                                                    <Card className="h-100">
                                                        <Card.Header>
                                                            <h6 className="mb-0">Configuration</h6>
                                                        </Card.Header>
                                                        <Card.Body>
                                                            <div className="mb-3">
                                                                <strong>Available Filters ({currentTemplate?.available_filters?.length || 0}):</strong>
                                                                <div className="mt-1">
                                                                    {currentTemplate?.available_filters?.map((filter, index) => (
                                                                        <Badge key={index} bg="secondary" className="me-1 mb-1">
                                                                            {filter}
                                                                        </Badge>
                                                                    )) || <span className="text-muted small">No filters defined</span>}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <strong>Required Permissions ({currentTemplate?.required_permissions?.length || 0}):</strong>
                                                                <div className="mt-1">
                                                                    {currentTemplate?.required_permissions?.map((permission, index) => (
                                                                        <Badge key={index} bg="warning" className="me-1 mb-1">
                                                                            {permission}
                                                                        </Badge>
                                                                    )) || <span className="text-muted small">No permissions required</span>}
                                                                </div>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            </Row>
                                        </div>
                                    </Tab>
                                    
                                    <Tab eventKey="sample" title="Sample Data">
                                        <div className="mt-3">
                                            {previewData.sample_data ? (
                                                <div className="table-responsive">
                                                    <Table striped bordered hover>
                                                        <thead>
                                                            <tr>
                                                                {Object.keys(previewData.sample_data[0] || {}).map(key => (
                                                                    <th key={key}>{key}</th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {previewData.sample_data.slice(0, 10).map((row, index) => (
                                                                <tr key={index}>
                                                                    {Object.values(row).map((value, cellIndex) => (
                                                                        <td key={cellIndex}>{String(value)}</td>
                                                                    ))}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                    {previewData.sample_data.length > 10 && (
                                                        <small className="text-muted">
                                                            Showing first 10 of {previewData.sample_data.length} records
                                                        </small>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="text-center py-4">
                                                    <BsInfoCircle className="display-4 text-muted mb-3" />
                                                    <p className="text-muted">No sample data available for this template</p>
                                                </div>
                                            )}
                                        </div>
                                    </Tab>
                                </Tabs>
                            </div>
                        ) : (
                            <div className="text-center py-4">
                                <Spinner animation="border" className="text-primary" />
                                <p className="mt-2">Loading preview...</p>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
                            Close
                        </Button>
                        <Button variant="primary" onClick={() => handleGenerateReport(currentTemplate)}>
                            <BsFileText className="me-1" />
                            Generate Report
                        </Button>
                    </Modal.Footer>
                </Modal>
                
                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title className="text-danger">
                            <BsExclamationTriangle className="me-2" />
                            Confirm Delete
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Are you sure you want to delete the template <strong>"{currentTemplate?.name}"</strong>?</p>
                        <Alert variant="warning" className="mb-0">
                            <small>
                                <strong>Warning:</strong> This action cannot be undone. 
                                Any reports using this template may be affected.
                            </small>
                        </Alert>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={confirmDelete}>
                            <BsTrash className="me-1" />
                            Delete Template
                        </Button>
                    </Modal.Footer>
                </Modal>
            </ServiceStatus>
        </ErrorBoundary>
    );
};

export default TemplateManager;